import type { StateContext } from '@ngxs/store';
import { type DynamicFlatNode, type IChannel, type ILaneConfig, isSameControlAsId, LaneConfig } from 'commons';
import { produce } from 'immer';
import { firstValueFrom } from 'rxjs';
import { isSameControlAs } from '../../../../components/dtis-multilane-viewer/multilane.utils';
import { findbyId } from '../../../../state_utils';
import { db } from '../../../db';
import * as a from '../actions';
import type { MultiViewStateModel } from '../MultiViewStateModel';
import type { SingleView } from '../SingleView';
import { getViewIndex, getViewIndexById } from '.';

export const filterNotMatchingId =
  (id: string | number) =>
  ({ id: remove_id }: { id: string | number }) =>
    remove_id !== id;

export namespace LaneConfigHandler {
  /**
   *
   * @param ctx
   * @param param1
   * @returns
   */
  export function removeById(ctx: StateContext<MultiViewStateModel>, { id }: a.RemoveLaneById) {
    const determineNextActive = (lanes: ILaneConfig[], formerActive: number) => (formerActive >= lanes.length - 1 ? formerActive - 1 : formerActive);

    const viewIndex = getViewIndex(ctx);
    const lanes = ctx.getState().views[viewIndex]?.lanes;
    if (!lanes) {
      return;
    }

    if (lanes.length <= 1) {
      return;
    }

    const toRemove = lanes.find(isSameControlAsId(id));
    const toRemoveI = lanes.findIndex(isSameControlAsId(id));
    if (!toRemove) {
      return;
    }

    ctx.setState(
      produce((draft) => {
        const view = draft.views[viewIndex] as SingleView;
        view.lanes = view.lanes.filter(filterNotMatchingId(id));
        if (toRemove.isActive && view.lanes.length > 0) {
          view.lanes[determineNextActive(view.lanes, toRemoveI)].isActive = true;
        }

        view.sensorTable = draft.views[viewIndex].sensorTable.filter((control) =>
          // remove from sensortable if the control is in the removed lane
          view.lanes.some(({ controls }) => controls.some(isSameControlAs(control)))
        );
      })
    );
    return ctx.dispatch(a.GenerateEChartConfig);
  }
  /**
   *
   * @param ctx
   * @param param1
   * @param store
   * @returns
   */
  export function removeByIndex(ctx: StateContext<MultiViewStateModel>, { index }: a.RemoveLaneByIndex) {
    const viewIndex = getViewIndex(ctx);
    const lanes = ctx.getState().views[viewIndex].lanes;
    if (lanes.length <= 1) {
      return;
    }

    const leftOverLanes: LaneConfig[] = lanes.filter(
      // if index is -1, remove the last lane
      (_, i) => i !== (index === -1 ? lanes.length - 1 : index)
    );
    const toRemove = lanes[index];
    if (!toRemove) {
      return;
    }

    ctx.setState(
      produce((draft) => {
        // biome-ignore lint/suspicious/noExplicitAny: immer hard to type correctly
        draft.views[viewIndex].lanes = leftOverLanes as any;
        if (toRemove.isActive) {
          draft.views[viewIndex].lanes[index + 1].isActive = true;
        }

        draft.views[viewIndex].sensorTable = draft.views[viewIndex].sensorTable.filter(
          (control) =>
            // remove from sensortable if the control is in the removed lane
            toRemove.controls.some(isSameControlAs(control)) &&
            // and not in any other lane
            !leftOverLanes.flat().some(({ controls }) => controls.some(isSameControlAs(control)))
        );
      })
    );
  }
  /**
   *
   * @param ctx
   * @param param1
   */
  export async function removeControl(ctx: StateContext<MultiViewStateModel>, { control, lane }: a.RemoveControlFromLaneConfig) {
    const viewIndex = getViewIndex(ctx);
    const channels: DynamicFlatNode<IChannel>[] = (await db.channelInfo.toArray()) as DynamicFlatNode<IChannel>[];

    if (viewIndex >= 0) {
      ctx.setState(
        produce(ctx.getState(), (draft) => {
          const view = draft.views[viewIndex] as SingleView;
          const laneIndex = view.lanes.findIndex(({ id }) => id === lane.id);
          if (laneIndex < 0) {
            return console.warn("Lane doesn't exist");
          }
          view.lanes[laneIndex].controls = view.lanes[laneIndex].controls.filter((c) => c.id !== control.id);
          view.lanes[laneIndex].config?.yAxises?.forEach((yAxis) => {
            if (yAxis.controls) {
              yAxis.controls = yAxis.controls.filter((cId) => cId !== control.id);
            }
          });
          view.sensorTable = view.sensorTable.filter((control) => draft.views[viewIndex].lanes.some(({ controls }) => controls.some(isSameControlAs(control))));
          updateTimestamp(view, laneIndex, channels);
        })
      );

      ctx.dispatch(a.GenerateEChartConfig);
    }
  }
  export async function addControlTo(ctx: StateContext<MultiViewStateModel>, { control, lane }: a.AddControlToLaneConfig) {
    const channels: DynamicFlatNode<IChannel>[] = (await db.channelInfo.toArray()) as DynamicFlatNode<IChannel>[];
    const state = ctx.getState();
    const viewIndex = getViewIndex(ctx);
    if (viewIndex < 0) {
      console.warn("View doesn't exist");
      return;
    }

    const laneIndex = lane ? state.views[viewIndex].lanes.findIndex(findbyId(lane)) : 0;
    if (!state.views[viewIndex].lanes[laneIndex]) {
      console.warn("Lane doesn't exist");
      return;
    }

    ctx.setState(
      produce(state, (draft) => {
        const view = draft.views[viewIndex] as SingleView;
        view.lanes[laneIndex].controls.push(control);
        updateTimestamp(view, laneIndex, channels);
      })
    );
    await firstValueFrom(ctx.dispatch(a.GenerateEChartConfig));
  }
  /**
   * Updates the first and last control timestamps for a specific lane in a view.
   *
   * @param view - The view containing the lanes and controls.
   * @param laneIndex - The index of the lane to update.
   */
  export function updateTimestamp(view: SingleView, laneIndex: number, channels: DynamicFlatNode<IChannel>[]) {
    const allEntities = view.lanes[laneIndex]?.controls.map((c) => channels.find(({ id }) => id === c.id)?.entity as IChannel).filter((d) => !!d);

    // find first and last timestamp of all entities
    if (allEntities?.length > 0) {
      const min = Math.min(...allEntities.map((ich) => new Date(ich.firstTimestamp).getTime()));
      const max = Math.max(...allEntities.map((ich) => new Date(ich.latestTimestamp).getTime()));
      view.lanes[laneIndex].firstControlTimestamp = new Date(min);
      view.lanes[laneIndex].lastControlTimestamp = new Date(max);
    }
  }

  export function update(ctx: StateContext<MultiViewStateModel>, { laneConfig }: a.UpdateLaneConfig) {
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        const viewIndex = getViewIndex(ctx);
        if (viewIndex >= 0) {
          const laneIndex = draft.views[viewIndex].lanes.findIndex(({ id }) => id === laneConfig.id);
          if (laneIndex >= 0) {
            draft.views[viewIndex].lanes[laneIndex] = {
              ...draft.views[viewIndex].lanes[laneIndex],
              // biome-ignore lint/suspicious/noExplicitAny: immer hard to type correctly
              ...(laneConfig as any),
            };
          }
        }
      })
    );
    ctx.dispatch(a.GenerateEChartConfig);
  }
  /**
   *
   * @param ctx
   * @param param1
   */
  export function add(ctx: StateContext<MultiViewStateModel>, { laneConfig, view }: a.AddLaneConfig) {
    const viewIndex = view ? getViewIndexById(ctx, view) : getViewIndex(ctx);
    const createLane = new LaneConfig(laneConfig);
    /**
     *
     * @param laneConfigs
     * @returns
     */
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        const lanes = draft.views[viewIndex].lanes as LaneConfig[];
        createLane.setNextLabel(lanes);
        draft.views[viewIndex].lanes.push(createLane.serialize);
      })
    );
    ctx.dispatch(a.GenerateEChartConfig);
    if (createLane) {
      ctx.dispatch(new a.SetLaneActive(createLane));
    }
  }
  /**
   * Reorders lanes by moving one lane from a source index to a target index.
   */
  export function reorder(ctx: StateContext<MultiViewStateModel>, { fromIndex, toIndex }: a.ReorderLanes) {
    const viewIndex = getViewIndex(ctx);
    const lanes = ctx.getState().views[viewIndex]?.lanes;
    if (!lanes || fromIndex < 0 || toIndex < 0 || fromIndex >= lanes.length || toIndex >= lanes.length || fromIndex === toIndex) {
      return;
    }

    ctx.setState(
      produce(ctx.getState(), (draft) => {
        const view = draft.views[viewIndex];
        const [moved] = view.lanes.splice(fromIndex, 1);
        view.lanes.splice(toIndex, 0, moved);
      })
    );
    ctx.dispatch(a.GenerateEChartConfig);
  }
  /**
   *
   * @param ctx
   * @param laneConfig
   */
  export function setLaneActive(ctx: StateContext<MultiViewStateModel>, laneConfig: LaneConfig) {
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        const viewIndex = getViewIndex(ctx);

        const laneIndex = draft.views[viewIndex].lanes.findIndex((l) => l.id === laneConfig.id);
        draft.views[viewIndex].lanes = draft.views[viewIndex].lanes.map(
          (l) => ({
            ...l,
            isActive: false,
          })
          // biome-ignore lint/suspicious/noExplicitAny: immer hard to type correctly
        ) as any;
        draft.views[viewIndex].lanes[laneIndex].isActive = true;
      })
    );
  }
}
